/**
 * Thermal FEA draft-time DOE axis paths (ENG-2606 / ENG-3002).
 *
 * Mirrors `khorium_models.sim_params._thermal_varying_paths` so the
 * frontend registry, matrix preview, and backend expander share one
 * vocabulary. Material strings stay off this set — they belong on DP
 * overrides, not `parameter_sweeps`. Time-schedule fields are never
 * sweepable; `initial_temperature_K` is admitted only on
 * `thermal_transient`.
 */

import type {
  ConvectionBc,
  HeatFluxBc,
  RadiationBc,
  TemperatureBc,
  ThermalConductionParams,
  ThermalTransientParams,
} from '@/api/generated/types.gen';
import type {
  ThermalConductionSolver,
  ThermalFeaSolver,
  ThermalTransientSolver,
} from './types';

type ThermalParams = ThermalConductionParams | ThermalTransientParams;

function isTemperature(
  bc: NonNullable<ThermalParams['boundary_conditions']>[number],
): bc is { type: 'temperature' } & TemperatureBc {
  return bc.type === 'temperature';
}

function isHeatFlux(
  bc: NonNullable<ThermalParams['boundary_conditions']>[number],
): bc is { type: 'heat_flux' } & HeatFluxBc {
  return bc.type === 'heat_flux';
}

function isConvection(
  bc: NonNullable<ThermalParams['boundary_conditions']>[number],
): bc is { type: 'convection' } & ConvectionBc {
  return bc.type === 'convection';
}

function isRadiation(
  bc: NonNullable<ThermalParams['boundary_conditions']>[number],
): bc is { type: 'radiation' } & RadiationBc {
  return bc.type === 'radiation';
}

/** Numeric paths this thermal case can carry on `parameter_sweeps`. */
export function thermalVaryingPaths(params: ThermalParams): string[] {
  const paths: string[] = [];
  if (params.template === 'thermal_transient') {
    paths.push('initial_temperature_K');
  }
  const faceKeyed = new Map<
    string,
    NonNullable<ThermalParams['boundary_conditions']>
  >();
  for (const bc of params.boundary_conditions ?? []) {
    if (bc.type === 'heat_point') continue;
    const list = faceKeyed.get(bc.face_id) ?? [];
    list.push(bc);
    faceKeyed.set(bc.face_id, list);
  }
  for (const [faceId, onFace] of faceKeyed) {
    if (!faceId || faceId.includes('.') || onFace.length !== 1) continue;
    const bc = onFace[0];
    if (isTemperature(bc) || isHeatFlux(bc)) {
      paths.push(`boundary_conditions.${faceId}.value`);
    } else if (isConvection(bc)) {
      paths.push(`boundary_conditions.${faceId}.h`);
      paths.push(`boundary_conditions.${faceId}.t_ambient`);
    } else if (isRadiation(bc)) {
      paths.push(`boundary_conditions.${faceId}.emissivity`);
      paths.push(`boundary_conditions.${faceId}.t_ambient`);
    }
  }
  return paths.sort();
}

/** Drop orphan `parameter_sweeps` keys after BC rename/delete. */
export function pruneThermalParameterSweeps(
  params: ThermalParams,
): ThermalFeaSolver {
  const template =
    params.template === 'thermal_transient'
      ? 'thermal_transient'
      : 'thermal_conduction';
  const withTemplate = { ...params, template } as ThermalFeaSolver;
  const sweeps = withTemplate.parameter_sweeps;
  if (!sweeps || Object.keys(sweeps).length === 0) return withTemplate;
  const allowed = new Set(thermalVaryingPaths(withTemplate));
  let changed = false;
  const next: NonNullable<ThermalParams['parameter_sweeps']> = {};
  for (const [path, value] of Object.entries(sweeps)) {
    if (allowed.has(path)) {
      next[path] = value;
    } else {
      changed = true;
    }
  }
  if (!changed) return withTemplate;
  return { ...withTemplate, parameter_sweeps: next } as ThermalFeaSolver;
}

/** Read the seed scalar at a thermal sweep path. */
export function readThermalSeed(
  params: ThermalParams,
  path: string,
): number | null {
  if (path === 'initial_temperature_K') {
    if (params.template !== 'thermal_transient') return null;
    return (params as ThermalTransientParams).initial_temperature_K;
  }
  const parts = path.split('.');
  if (parts[0] !== 'boundary_conditions' || parts.length !== 3) return null;
  const [, faceId, field] = parts;
  for (const bc of params.boundary_conditions ?? []) {
    if (bc.type === 'heat_point') continue;
    if (bc.face_id !== faceId) continue;
    if (
      (isTemperature(bc) || isHeatFlux(bc)) &&
      field === 'value' &&
      'value' in bc
    ) {
      return bc.value;
    }
    if (isConvection(bc)) {
      if (field === 'h') return bc.h;
      if (field === 't_ambient') return bc.t_ambient;
    }
    if (isRadiation(bc)) {
      if (field === 'emissivity') return bc.emissivity;
      if (field === 't_ambient') return bc.t_ambient;
    }
  }
  return null;
}

/** Preserve typed return for callers that still expect conduction-only. */
export type { ThermalConductionSolver, ThermalTransientSolver };
